import React from "react";
import { Radio } from "@mui/material";
import AddShoppingCartOutlined from "@mui/icons-material/AddShoppingCartOutlined";

// Redux
import { useUserReducer } from "@/main/store/user";

// Models
import { IPartInCart, IPartsCard, ISupplierCard } from "../cardShopping";

// Styles
import * as S from "../styles";

// Utils & Enums
import { formatCurrency } from "@/main/services/utilsNumber";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";

// Components
import FavoriteButton from "../../favoriteButton/favoriteButton";
import CountControl from "../../countControl/countControl";
import NotQuotedOrAwaitingAnswerInfo from "../notQuotedOrAwaitingAnswerInfo";
import PriceDiffPercent from "../../priceDiffPercent/priceDiffPercent";
import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { Controls } from "@/main/services/constants/constantsControl";
import StarRatingPartially from "../../starRating/starRatingPartially";

interface IColumnCartLayout {
	uniqueIdPart: string;
	part: IPartsCard;
	supplier?: ISupplierCard;
	isFavorite: boolean;
	isShowSupplier: boolean;
	integration: boolean;
	isQuoteOpen: boolean;
	isQuoteManual: boolean;
	isLoadingAddToCart: boolean;
	isBlocked?: boolean;
	partInCart?: IPartInCart;
	optionSelectedPart?: string;
	callIncrementCart: (part: IPartInCart, count: number) => void;
	callDecrementCart: (part: IPartInCart, count: number) => void;
	handleClickCartSupplier: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	handleToggleFavorite: () => void;
	handleClickAddCartSupplierRecommend: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	handleSelectPart?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
	notQuoted?: boolean;
}

export function ColumnCartLayout({
	uniqueIdPart,
	part,
	supplier,
	isFavorite,
	isShowSupplier,
	integration,
	isQuoteOpen,
	isQuoteManual,
	isLoadingAddToCart,
	isBlocked = false,
	partInCart,
	optionSelectedPart = undefined,
	callIncrementCart,
	callDecrementCart,
	handleClickCartSupplier,
	handleToggleFavorite,
	handleClickAddCartSupplierRecommend,
	handleSelectPart = () => {},
}: IColumnCartLayout) {
	const userReducer = useUserReducer();
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const showButtonAddCart = isQuoteManual ? isShowControlByElementId(Controls.IdAddPartToCart) : true;

	function renderPartTypeAndPartBrand(part: IPartsCard) {
		if (!part.idPartType || !part.partType) {
			return <div className="products-list__column products-list__column--partyType"></div>;
		}

		if (part.idPartType !== PartTypeEnum.OEM) {
			return (
				<div className="products-list__column products-list__column--partyType">{`${part.partType} ${
					part.partBrandDescription ? `(${part.partBrandDescription})` : ""
				}`}</div>
			);
		}

		return <div className="products-list__column products-list__column--partyType">{part.partType}</div>;
	}

	return (
		<S.ContainerCardShoppingRow active={!userReducer.userPreference.preferences.isViewCard}>
			{isShowSupplier ? (
				<>
					{part.image && (
						<div className="products-list__column products-list__column--image">
							<img src={part.image} alt="" />
						</div>
					)}
					{supplier && (
						<div className="products-list__column products-list__column--supplier">
							<span title={supplier?.name}>{supplier?.name}</span>
							<FavoriteButton
								isFavorite={isFavorite}
								onToggleFavorite={handleToggleFavorite}
								notClickable={isBlocked}
							/>
						</div>
					)}
					{!isBlocked && (
						<>
							<div className="products-list__column products-list__column--sku">{part.partNumber || ""}</div>
							{renderPartTypeAndPartBrand(part)}
							<div className="products-list__column products-list__column--product">{part.title}</div>
							<div className="products-list__column products-list__column--deliveryTime">{part.deliveryTime}</div>
							<div className="products-list__column products-list__column--rating">
								<StarRatingPartially rating={!isNullUndefined(part.rating) ? part.rating : 0} />
							</div>
							{part.price !== undefined ? (
								<div className="products-list__column products-list__column--price">
									{showPrices && formatCurrency(part.price)}
									{!isNullUndefined(part.priceDiffPercent) && (
										<PriceDiffPercent diffPercent={Number(part.priceDiffPercent)} />
									)}
								</div>
							) : (
								<div className="products-list__column products-list__column--price"></div>
							)}

							<div className="products-list__column products-list__column--addToCart">
								{optionSelectedPart !== undefined ? (
									<Radio
										checked={optionSelectedPart === uniqueIdPart}
										onChange={handleSelectPart}
										color="primary"
										value={uniqueIdPart}
										name="radio-button-selected-part"
									/>
								) : partInCart ? (
									<CountControl
										isBlocked={(integration && isQuoteOpen) || (integration && isQuoteManual)}
										initialCount={partInCart.quantity}
										maxCount={partInCart.stock}
										onDecrement={(count) => callDecrementCart(partInCart, count)}
										onIncrement={(count) => callIncrementCart(partInCart, count)}
									/>
								) : (
									showButtonAddCart && (
										<S.IconButtonCart
											onClick={handleClickCartSupplier}
											id={`id-createOrder-button-cart-${part.title}`}
											disabled={(integration && isQuoteOpen) || (integration && isQuoteManual)}
										>
											<AddShoppingCartOutlined fontSize="medium" />
										</S.IconButtonCart>
									)
								)}
							</div>
						</>
					)}

					{part.shipingIntegrationIsUnavailable && part.shipingIntegrationIsUnavailable && (
						<div className="products-list__column products-list__column--awaitingAnswer">
							<S.AwaitingAnswer>
								<div className="d-flex flex-column">
									{getTranslateByKey("information.unavailableErrorConnectionToShippingCompany")}
								</div>
							</S.AwaitingAnswer>
						</div>
					)}

					{isBlocked && !(part.shipingIntegrationIsUnavailable && part.shipingIntegrationIsUnavailable) && (
						<div className="products-list__column products-list__column--awaitingAnswer">
							<S.AwaitingAnswer>
								<NotQuotedOrAwaitingAnswerInfo
									reasonDescription={part.reasonDescription}
									reason={part.reason}
									notQuoted={part.notQuoted}
									isQuoteManual={isQuoteManual}
									isQuoteOpen={isQuoteOpen}
								/>
							</S.AwaitingAnswer>
						</div>
					)}
				</>
			) : (
				<>
					<div className="products-list__column products-list__column--partName">{part.title}</div>
					<div className="products-list__column products-list__column--category">{part.category}</div>
					<div className="products-list__column products-list__column--subCategory">{part.subCategory}</div>
					<div className="products-list__column products-list__column--addToCart">
						{partInCart ? (
							<CountControl
								isBlocked={(integration && isQuoteOpen) || !showButtonAddCart}
								initialCount={partInCart.quantity}
								maxCount={partInCart.hasMoreSuppliers ? 999 : partInCart.stock}
								onDecrement={(count) => callDecrementCart(partInCart, count)}
								onIncrement={(count) => callIncrementCart(partInCart, count)}
							/>
						) : (
							showButtonAddCart && (
								<S.IconButtonCart
									onClick={handleClickAddCartSupplierRecommend}
									className="ignore-click"
									disabled={(integration && isQuoteOpen) || !showButtonAddCart || isLoadingAddToCart}
								>
									<AddShoppingCartOutlined fontSize="medium" />
								</S.IconButtonCart>
							)
						)}
					</div>
				</>
			)}
		</S.ContainerCardShoppingRow>
	);
}
